import { RunTimelineInput } from 'src/components/dispatch/autoDispatch/useAutoDispatchController';
import { parseUnassignedIds } from 'src/components/dispatch/parseUnassignedIds';
import { UnassignedItem } from 'src/entities/dispatch';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { TimelineRequestEntity } from 'src/entities/TimelineRequest.entity';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';
import { buildTimelineRequestEntities, ComputedTimelineOp } from 'src/pages/ConvenienceDispatches/buildTimelineRequestEntities';
import { buildTimelineRunInput } from 'src/pages/ConvenienceDispatches/buildTimelineRunInput';
import { coordKeyOf } from 'src/pages/ConvenienceDispatches/placeKeys';

/** 仮想回転(新規追加) は shift_truck_id 未確定を表す marker */
const VIRTUAL_STID = -1;

/**
 * 楽観更新で使う地点キー。導出は placeKeys.ts（vehicleRowVM と共有）。
 * ここだけ「配送リストの地点表示」設定に追随し忘れると、ドロップ直後だけカードが
 * 2 枚に割れて再取得で 1 枚に戻る。
 */
const loadingCoordKey = (lr: ShipmentOrderLegRunForPlanEntity): string =>
  coordKeyOf('loading', lr, `lr-${lr.id ?? 'x'}-load`);

const unloadingCoordKey = (lr: ShipmentOrderLegRunForPlanEntity): string =>
  coordKeyOf('unloading', lr, `lr-${lr.id ?? 'x'}-unload`);

// 地点間の想定移動時間（秒）。実距離は考慮せず、仕様上の固定値。
const TRAVEL_SECONDS_BETWEEN_PLACES = 30 * 60;

function addSecondsToDate(date: Date, seconds: number): Date {
  return new Date(date.getTime() + Math.max(0, seconds) * 1000);
}

/**
 * 1 cycle 分の legRuns に対し、積→降 の arrival_at/departure_at を生成する。
 *
 * 仕様:
 * - baseline は呼び出し側で算出して渡す (前 cycle の最終 departure + 30分 / shift 開始時刻 等)
 * - 積み: loading 地点 (placeKey) ごとに集約。同地点の複数 legRun は同一 arrival/departure
 * - 別地点の積み: 前地点 departure + 30分 → 当地点 arrival
 * - 降り 1件目: 最後の積み departure + 30分、以降は積みと同じ規則
 *
 * 時間は配車アルゴリズムで再計算されるため正確である必要はなく、並びの整合性のみが重要。
 */
function computeOpsForCycle(args: {
  stid: number;
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  cycle: number;
  baseline: Date;
}): ComputedTimelineOp[] {
  const { stid, legRuns: groupLegRuns, cycle, baseline } = args;
  if (groupLegRuns.length === 0) return [];

  const result: ComputedTimelineOp[] = [];

  // 積みを地点ごとに集約 (出現順維持)。
  // Why 座標のまま: ここで作るのは送信する arrival/departure。「配送リストの地点表示」は
  // 表示の設定であって、同じ場所での作業を名称ごとに別停車として送る指示ではない
  const loadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const loadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = loadingCoordKey(lr);
    const idx = loadingPlaceIdx.get(key);
    if (idx != null) loadingGroups[idx].push(lr);
    else {
      loadingPlaceIdx.set(key, loadingGroups.length);
      loadingGroups.push([lr]);
    }
  });

  let lastDeparture = baseline;
  loadingGroups.forEach((group, groupIdx) => {
    const arrival = groupIdx === 0
      ? baseline
      : addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.loading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        // 同一地点 2件目以降は 1件目の departure_at に揃える (到着後すぐ出発扱い)
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '積',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  // 降りを地点ごとに集約（積みと同じ理由で座標のまま）
  const unloadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const unloadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = unloadingCoordKey(lr);
    const idx = unloadingPlaceIdx.get(key);
    if (idx != null) unloadingGroups[idx].push(lr);
    else {
      unloadingPlaceIdx.set(key, unloadingGroups.length);
      unloadingGroups.push([lr]);
    }
  });

  unloadingGroups.forEach((group) => {
    const arrival = addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.unloading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '降',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  return result;
}

/**
 * legRun の所在は (stid, cycle) のペアで表す。
 * - stid: サーバ既存 delivery の id / VIRTUAL_STID (-1) / null (未割当)
 * - cycle: 回転番号（未割当時は null）
 *
 * cycle 情報を含めることで、同一 truck 内での回転移動も検知できる。
 */
type LegRunLocation = { stid: number | null; cycle: number | null };

export type AssignmentTimelineInputSource = {
  deliveries: PlanningsDeliveryWithLegRunEntity[];
  /** **表示中の**（上書き適用後の）行グループ。サーバ由来のものではない */
  rowGroups: VehicleRowGroup[];
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  serverUnassigned: UnassignedItem[];
  /** 上書きが無ければ null。その場合は serverUnassigned が現在の未割当 */
  overrideUnassigned: UnassignedItem[] | null;
  rowIdToShiftTruckId: ReadonlyMap<string, number>;
  /** 仮想回転 (回転追加) の行から、そのトラックの代表 delivery を引くための補助 */
  truckIdToDeliveryId: ReadonlyMap<number, number>;
  startOn: string;
};

/**
 * 表示中の盤面とサーバの配車結果を突き合わせ、自動配車 (timelinePlanning) へ送る入力を組む。
 *
 * 変更された legRun が 1 件も無ければ `null`。呼び出し側はそのとき自動配車を撃たない。
 * 実行 (`controller.runTimeline`) と debounce は呼び出し側の責務で、ここは入力を作るだけ。
 *
 * Why not hook: 入力は「表示中の行・未割当・サーバの配車結果」だけから決まるので、
 * React の state を持つ必要が無い。純関数にしておくと、D&D を 1 回も起こさずに
 * リクエストの中身（誰の stid にどの順で積むか）を単体テストで固定できる。
 */
export function buildAssignmentTimelineInput(
  source: AssignmentTimelineInputSource,
): RunTimelineInput | null {
  const {
    deliveries, rowGroups, legRuns, serverUnassigned, overrideUnassigned,
    rowIdToShiftTruckId, truckIdToDeliveryId, startOn,
  } = source;

  // 元の legRun → 所在マップ（既存 operation のある legRun のみ）
  const originalLocation = new Map<number, LegRunLocation>();
  deliveries.forEach((d) => {
    d.operations.forEach((op) => {
      const lrId = op.shipmentOrderLegRunId;
      // 同じ legRun の op が複数ある場合、最初の cycle を採用（全て同じ cycle であるはず）
      if (!originalLocation.has(lrId)) {
        originalLocation.set(lrId, { stid: d.id, cycle: op.cycle >= 1 ? op.cycle : 1 });
      }
    });
  });

  // 現在（override 反映後）の legRun → 所在マップ
  const currentLocation = new Map<number, LegRunLocation>();
  const currentUnassigned = overrideUnassigned ?? serverUnassigned;
  parseUnassignedIds(currentUnassigned.map((u) => u.id)).forEach((lrId) => {
    currentLocation.set(lrId, { stid: null, cycle: null });
  });
  rowGroups.forEach((g) => g.rows.forEach((r) => {
    // 仮想回転 (回転追加) は対応する delivery.id が rowIdToShiftTruckId に無いが、
    // 同じ truckId に既存 delivery があればそこへ new cycle として寄せる
    const stid = rowIdToShiftTruckId.get(r.rowId)
      ?? truckIdToDeliveryId.get(r.truckId)
      ?? VIRTUAL_STID;
    const cycle = r.rotationNumber;
    r.cards.forEach((c) => {
      // 所在判定には delivery カード（降地）のみを用いる。
      // loading カードにも同じ legRunId が含まれているため、両方を見ると別トラックへの
      // 移動時に「移動元の loading」と「移動先の delivery」で currentLocation が上書き合戦になる。
      if (c.type !== 'delivery') return;
      // no-delivery / fr-template は「まだ割り当てられていない」状態なので current location 対象外
      if (c.status === 'no-delivery' || c.status === 'fr-template') return;
      (c.legRunIds ?? []).forEach((lrId) => {
        currentLocation.set(lrId, { stid, cycle });
      });
    });
  }));

  // 変更があった legRun を特定（stid または cycle が変わったもの）
  const allLegRunIds = new Set<number>();
  originalLocation.forEach((_, id) => allLegRunIds.add(id));
  currentLocation.forEach((_, id) => allLegRunIds.add(id));
  const changedLegRunIds = new Set<number>();
  allLegRunIds.forEach((lrId) => {
    const orig = originalLocation.get(lrId);
    const now = currentLocation.get(lrId);
    const origStid = orig?.stid ?? null;
    const nowStid = now?.stid ?? null;
    const origCycle = orig?.cycle ?? null;
    const nowCycle = now?.cycle ?? null;
    if (origStid !== nowStid || origCycle !== nowCycle) changedLegRunIds.add(lrId);
  });

  // 同一 (stid, cycle) 内での順序変更も検知する。stid / cycle 変化が無くても、
  // override の表示順が server の operations の順序と異なれば「順序変更あり」として
  // その row 内の全 legRun を changed 扱いにする (timelinePlanning に並び順を伝える)。
  rowGroups.forEach((g) => g.rows.forEach((r) => {
    const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId) ?? VIRTUAL_STID;
    if (stid === VIRTUAL_STID) return;
    const cycle = r.rotationNumber;
    const currentOrder: number[] = [];
    r.cards.forEach((c) => {
      if (c.type !== 'delivery') return;
      if (c.status === 'no-delivery' || c.status === 'fr-template') return;
      (c.legRunIds ?? []).forEach((lrId) => {
        if (!currentOrder.includes(lrId)) currentOrder.push(lrId);
      });
    });
    if (currentOrder.length === 0) return;
    // server 側: 同じ (stid, cycle) に属する 降 op を arrival_at 順で並べた legRun id 列
    const delivery = deliveries.find((d) => d.id === stid);
    if (!delivery) return;
    const serverOrder: number[] = delivery.operations
      .filter((op) => op.action === '降' && (op.cycle >= 1 ? op.cycle : 1) === cycle)
      .slice()
      .sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())
      .map((op) => op.shipmentOrderLegRunId)
      .filter((id, idx, arr) => arr.indexOf(id) === idx);
    if (serverOrder.length === 0) return;
    const sameOrder = currentOrder.length === serverOrder.length
      && currentOrder.every((id, i) => id === serverOrder[i]);
    if (!sameOrder) {
      currentOrder.forEach((id) => changedLegRunIds.add(id));
    }
  }));

  console.log('[dispatch-assignment] changedLegRunIds', Array.from(changedLegRunIds));
  if (changedLegRunIds.size === 0) return null;

  // 「row → unassigned」(配車済み → 未割当に戻す) ドロップを抽出する。
  // 実際の reset 実行・isUnassignOnly 判定・その後の分岐は buildTimelineRunInput +
  // controller.runTimeline が持つ（Task 8 で移設。「未割当に戻す」ボタンと同等の挙動）
  const unassignedDropIds: number[] = [];
  changedLegRunIds.forEach((lrId) => {
    const orig = originalLocation.get(lrId);
    const now = currentLocation.get(lrId);
    // 元は配車済 (stid > 0) → 現在は unassigned (stid == null)
    if (orig?.stid != null && orig.stid > 0 && (now == null || now.stid == null)) {
      unassignedDropIds.push(lrId);
    }
  });

  return buildTimelineRunInput({
    changedLegRunIds,
    unassignedDropIds,
    buildBody: (remaining) => {
      // 仮想回転に割当てられた legRun は timeline に送れない（backend delivery 未確定）
      const virtualChangedIds = Array.from(remaining).filter(
        (lrId) => currentLocation.get(lrId)?.stid === VIRTUAL_STID,
      );
      if (virtualChangedIds.length > 0) {
        console.warn('[dispatch-assignment] 仮想回転の割当は backend に反映不可（spec調整要）', virtualChangedIds);
      }

      // 影響を受けた delivery (stid) の集合: 変更 legRun の 移動元 / 移動先 を union
      // 影響を受けた delivery については (cycle 順 → 積→降 順) で全 ops を再構築する
      const affectedStids = new Set<number>();
      remaining.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const current = currentLocation.get(lrId);
        if (orig?.stid != null && orig.stid > 0) affectedStids.add(orig.stid);
        if (current?.stid != null && current.stid > 0) affectedStids.add(current.stid);
      });

      // 既存 operation の (lrId, action) → operation id 索引。リクエストの id 引き継ぎに使う
      const existingOpKeyOf = (lrId: number, action: string) => `${lrId}-${action}`;
      const existingOpIdIndex = new Map<string, number>();
      deliveries.forEach((d) => {
        d.operations.forEach((op) => {
          existingOpIdIndex.set(existingOpKeyOf(op.shipmentOrderLegRunId, op.action), op.id);
        });
      });
      const existingOperationIdOf = (lrId: number, action: string) => (
        existingOpIdIndex.get(existingOpKeyOf(lrId, action))
      );

      // timeline_operations 構築
      // 仕様: 影響 delivery については (stid, cycle) 単位で表示順に並べ、各グループ内で 積→降 を
      //       時系列が単調増加するように再生成する。例:
      //       移動前 cycle1=[A積,B積,C積,A降,B降,C降]
      //       A を cycle2 にドロップ後の display: cycle1=[B,C], cycle2=[A]
      //       → request: [B積, C積, B降, C降, A積, A降] (cycle1 全て → cycle2 全て、時刻も単調)
      const fallbackStart = new Date(`${startOn}T00:00:00+09:00`);
      const lastDepartureByStid = new Map<number, Date>();
      const timelineEntities: TimelineRequestEntity[] = [];

      rowGroups.forEach((g) => g.rows.forEach((r) => {
        // 仮想回転は truckId の代表 delivery.id にフォールバック
        const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId);
        if (stid == null) return;
        if (!affectedStids.has(stid)) return;
        const cycle = r.rotationNumber;

        // 当該 row の delivery カード列から legRun を表示順で抽出 (重複除外)
        const seen = new Set<number>();
        const legRunsInOrder: ShipmentOrderLegRunForPlanEntity[] = [];
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            if (seen.has(lrId)) return;
            const lr = legRuns.find((x) => x.id === lrId);
            if (!lr) return;
            seen.add(lrId);
            legRunsInOrder.push(lr);
          });
        });
        if (legRunsInOrder.length === 0) return;

        // baseline: 同 stid の前 cycle の最終 departure + 30分 → なければ delivery 開始 → fallback
        const prior = lastDepartureByStid.get(stid);
        const delivery = deliveries.find((d) => d.id === stid);
        const baseline = prior
          ? addSecondsToDate(prior, TRAVEL_SECONDS_BETWEEN_PLACES)
          : (delivery ? new Date(delivery.startAt) : fallbackStart);

        const ops = computeOpsForCycle({ stid, legRuns: legRunsInOrder, cycle, baseline });
        if (ops.length === 0) return;

        // 次 cycle の baseline 用に最終 departure を track
        const last = ops.reduce(
          (max, o) => (o.departure_at.getTime() > max.getTime() ? o.departure_at : max),
          ops[0].departure_at,
        );
        lastDepartureByStid.set(stid, last);

        timelineEntities.push(...buildTimelineRequestEntities(ops, existingOperationIdOf));
      }));

      // 空になった delivery の id
      // - 元から operations が空の delivery は除外（シフトのみの配車枠）
      // - 元は legRun を持っていたが、現在 current Location に誰も居ない delivery だけ対象
      const originallyPopulatedDeliveryIds = new Set<number>();
      deliveries.forEach((d) => {
        if (d.operations.length > 0) originallyPopulatedDeliveryIds.add(d.id);
      });
      const deliveryIdsWithOps = new Set<number>();
      currentLocation.forEach((loc) => {
        if (loc.stid != null && loc.stid > 0) deliveryIdsWithOps.add(loc.stid);
      });
      const emptyDeliveryIds: number[] = [];
      originallyPopulatedDeliveryIds.forEach((id) => {
        if (!deliveryIdsWithOps.has(id)) emptyDeliveryIds.push(id);
      });

      console.log('[dispatch-assignment] request', {
        timelineEntities: timelineEntities.length,
        emptyDeliveryIds: emptyDeliveryIds.length,
        virtualChangedIds,
      });

      return { timelineEntities, emptyDeliveryIds };
    },
  });
}
